'use client';

import { useEffect, useState, useRef } from 'react';
import { DodoPayments } from 'dodopayments-checkout';
import Icon from '@/components/ui/AppIcon';

interface DodoCheckoutButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Molecule Component: DodoCheckoutButton
 * 
 * A button component that integrates with Dodo Payments Overlay Checkout.
 * Handles SDK initialization, checkout opening, and event handling.
 */
export default function DodoCheckoutButton({
  onSuccess,
  onError,
  disabled = false,
  className = '',
  children,
}: DodoCheckoutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const isInitializingRef = useRef(false);

  // Initialize Dodo Payments SDK once
  useEffect(() => {
    if (isInitializingRef.current || isInitialized) return;

    isInitializingRef.current = true;

    try {
      // Check if DodoPayments is available
      if (typeof window === 'undefined' || !DodoPayments) {
        console.warn('DodoPayments SDK not available');
        setIsInitialized(false);
        return;
      }

      const mode = process.env.NEXT_PUBLIC_DODO_MODE === 'live' ? 'live' : 'test';

      DodoPayments.Initialize({
        mode,
        onEvent: (event) => {
          console.log('Dodo Checkout event:', event);

          switch (event.event_type) {
            case 'checkout.opened':
              setIsLoading(false);
              break;

            case 'checkout.closed':
              setIsLoading(false);
              // Check if payment was successful by checking URL params
              // The webhook will handle credit addition, but we can refresh credits here
              if (window.location.search.includes('success=true')) {
                if (onSuccess) {
                  onSuccess();
                }
              }
              break;

            case 'checkout.redirect':
              // Payment successful, user will be redirected to return_url
              setIsLoading(false);
              // The redirect will happen automatically, but we can prepare for success
              // The webhook will handle credit addition
              break;

            case 'checkout.customer_details_submitted':
              // Customer details submitted, payment processing
              break;

            case 'checkout.payment_page_opened':
              // Payment page opened
              break;

            case 'checkout.error':
              setIsLoading(false);
              const errorMessage = typeof event.data?.message === 'string' 
                ? event.data.message 
                : 'An error occurred during checkout';
              console.error('Checkout error:', errorMessage);
              if (onError) {
                onError(errorMessage);
              } else {
                alert(`Checkout error: ${errorMessage}`);
              }
              break;

            default:
              // Handle other events if needed
              console.log('Unhandled checkout event:', event.event_type);
              break;
          }
        },
      });

      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize Dodo Payments SDK:', error);
      if (onError) {
        onError('Failed to initialize payment system');
      }
    } finally {
      isInitializingRef.current = false;
    }
  }, [isInitialized, onSuccess, onError]);

  const handleCheckout = async () => {
    if (!isInitialized || isLoading || disabled) return;

    setIsLoading(true);

    try {
      // Fetch checkout URL from backend
      const response = await fetch('/api/payments/create-checkout', {
        method: 'POST',
      });

      const data = await response.json();

      if (!response.ok || !data.checkoutUrl) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Open checkout overlay
      await DodoPayments.Checkout.open({
        checkoutUrl: data.checkoutUrl,
      });
    } catch (error: any) {
      console.error('Failed to open checkout:', error);
      setIsLoading(false);
      const errorMessage = error.message || 'Failed to start checkout. Please try again.';
      if (onError) {
        onError(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  return (
    <button
      onClick={handleCheckout}
      disabled={!isInitialized || isLoading || disabled}
      className={className}
    >
      {children || (
        <>
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Icon name="CreditCardIcon" size={20} />
              <span>Purchase Credits</span>
            </>
          )}
        </>
      )}
    </button>
  );
}

